// src/components/SelectedFieldPanel.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Button } from "flowbite-react";
import FieldSelector from "./FieldSelector";
import { v4 as uuid } from "uuid";

export default function SelectedFieldPanel({
  selectedField,
  mapping,
  setMapping,
  fieldsMap,
  defaultFontSize,
}) {
  // Local rows correspond to mapping[selectedField]
  const [rows, setRows] = useState([]);
  const [fontSize, setFontSize] = useState("");

  // Whenever the selected field changes (or its mapping updates), load its rows
  const handleAdd = useCallback(() => {
    if (!selectedField) return;
    if (!window.confirm("Add another field to " + selectedField + "?")) return;
    const newId = uuid();
    const rowNumber = rows.length + 1;
    const newRow = { order: rowNumber, id: newId, mode: 'dynamic', field: '', subfield: '', value: '' };

    setRows(rs => [...rs, newRow]);
    setMapping(prev => {
      const fieldMap = prev[selectedField] || {};
      return {
        ...prev,
        [selectedField]: {
          ...fieldMap,
          [newId]: { ...newRow }
        }
      };
    });
  }, [rows.length, selectedField, setMapping]);

  useEffect(() => {
    if (!selectedField) {
      setRows([]);
      return;
    }

    // Pull existing mapping entries for this field
    const fieldMap = mapping[selectedField] || {};

    // Load fontSize from _meta
    const meta = fieldMap["_meta"] || {};
    setFontSize(meta.fontSize ?? "");

    const loaded = Object.entries(fieldMap)
      .filter(([id]) => id !== "_meta")
      .map(([id, data], index) => ({
        id,
        order: data.order ?? index + 1,
        mode: data.mode || 'dynamic',
        field: data.field || '',
        subfield: data.subfield || '',
        subSubfield: data.subSubfield || '',
        subSubSubfield: data.subSubSubfield || '',
        value: data.value || '',
      }));

    // If no entries yet, create one blank row
    if (loaded.length === 0) {
      const newId = uuid();
      const newRow = {
        id: newId,
        order: 1,
        mode: 'dynamic',
        field: '',
        subfield: '',
        value: ''
      };

      // 1) Set local rows to exactly this one row
      setRows([newRow]);

      // 2) Mirror it into your mapping so future switches “see” an order=1
      setMapping(prev => {
        const fm = prev[selectedField] || {};
        return {
          ...prev,
          [selectedField]: {
            ...fm,
            [newId]: newRow
          }
        };
      });
      return;
    }

    setRows(loaded);
  }, [selectedField, mapping, handleAdd, setMapping]);

  if (!selectedField) {
    return (
      <div className="w-72 h-full flex-shrink-0 flex items-center justify-center text-gray-400">
        Click a region to edit
      </div>
    );
  }

  function handleFontSizeChange(e) {
    const val = e.target.value;
    setFontSize(val);
    setMapping(prev => {
      const fieldMap = prev[selectedField] || {};
      return {
        ...prev,
        [selectedField]: {
          ...fieldMap,
          "_meta": {
            ...(fieldMap["_meta"] || {}),
            fontSize: val === "" ? "" : Number(val)
          }
        }
      };
    });
  }

  function handleChange(rowId, key, val) {
    // Update local rows
    setRows(rs =>
      rs.map(r => (r.id === rowId ? { ...r, [key]: val } : r))
    );

    // Update global mapping
    setMapping(prev => {
      const fieldMap = prev[selectedField] || {};
      return {
        ...prev,
        [selectedField]: {
          ...fieldMap,
          [rowId]: {
            ...fieldMap[rowId],
            [key]: val
          }
        }
      };
    });
  }

  function handleRemove(idToRemove) {
    if (!window.confirm("Remove this field?")) return;

    setRows(rs => rs.filter(r => r.id !== idToRemove));

    setMapping(prev => {
      const fieldMap = { ...prev[selectedField] } || {};
      delete fieldMap[idToRemove];
      return {
        ...prev,
        [selectedField]: fieldMap
      };
    });
  }

  // Sort rows by their 'order' property before rendering
  const sortedRows = [...rows].sort((a, b) => a.order - b.order);

  return (
    <div className="flex flex-col h-full space-y-4">
      <h3 className="text-lg font-semibold">{selectedField}</h3>
      {/* Font size setting */}
      <div className="flex items-center gap-2">
        <label htmlFor="fontSize" className="text-sm font-medium text-gray-700 whitespace-nowrap">
          Font Size
        </label>
        <input
          id="fontSize"
          type="number"
          min={1}
          max={72}
          value={fontSize}
          onChange={handleFontSizeChange}
          placeholder={defaultFontSize ?? ""}
          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
        />
        {defaultFontSize != null && (
          <span className="text-xs text-gray-400">default: {defaultFontSize}</span>
        )}
      </div>
      {/* Scrollable container for fields */}
      <div className="flex-1 overflow-y-auto space-y-4">
        {sortedRows.map(row => (
          <FieldSelector
            key={row.id}
            index={row.id}
            data={row}
            onChange={handleChange}
            onRemove={handleRemove}
            fieldsMap={fieldsMap}
          />
        ))}
      </div>
      <Button
        color="light"
        size="sm"
        onClick={handleAdd}
        className="w-full flex items-center justify-center gap-2"
      >
        + Add Another Field
      </Button>
    </div>
  );
}
